'use client';

import { useOKRs } from '@/domain/okrs/hooks';
import { type OKR } from '@/domain/okrs/type';
import { type OKRAdd } from '@/domain/products/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { getImpact } from '@/features/roadmap/utils/enum';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { FormControl } from '@andritzot/metris-web-ui/inputs/form-control';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Select } from '@andritzot/metris-web-ui/inputs/select';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { DataGrid, type GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';
import { MenuItem } from '@andritzot/metris-web-ui/navigation/menu/menu-item';
import { Add } from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCallback, useMemo } from 'react';

const IMPACT_OPTIONS = [
  { id: 0, impact: 'Low' },
  { id: 1, impact: 'Medium' },
  { id: 2, impact: 'High' },
] as const;

interface BusinessImpactedSectionProps {
  viewOnly: boolean;
}

type OkrRow = OKRAdd & { rowKey: number };

export function BusinessImpactedSection({ viewOnly }: Readonly<BusinessImpactedSectionProps>) {
  const businessImpact = useRoadmapEditStore((s) => s.editableRoadMap.okrs);
  const { data: okrs = [] } = useOKRs();

  const selectedIds = useMemo(() => businessImpact.map((o) => o.id), [businessImpact]);

  const rows: OkrRow[] = useMemo(() => businessImpact.map((o, i) => ({ ...o, rowKey: i })), [businessImpact]);

  const updateStore = (updated: OKRAdd[]) =>
    useRoadmapEditStore.setState((state) => ({
      editableRoadMap: { ...state.editableRoadMap, okrs: updated },
      isStoreDirty: true,
    }));

  const handleUpdate = useCallback(
    (index: number, patch: Partial<OKRAdd>) => {
      updateStore(businessImpact.map((o, i) => (i === index ? { ...o, ...patch } : o)));
    },
    [businessImpact],
  );

  const handleDelete = useCallback(
    (index: number) => {
      updateStore(businessImpact.filter((_, i) => i !== index));
    },
    [businessImpact],
  );

  const handleAdd = () => {
    updateStore([...businessImpact, { id: 0, impact: { id: 0, impact: getImpact(0) } }]);
  };

  const columns = useMemo<GridColDef<OkrRow>[]>(() => {
    const nameCol: GridColDef<OkrRow> = {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const availableOptions = okrs.filter((o) => o.id === row.id || !selectedIds.includes(o.id));
        const selectedOkr = availableOptions.find((o) => o.id === row.id) ?? null;
        return (
          <Autocomplete<OKR, false, false, false>
            options={availableOptions}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={selectedOkr}
            onChange={(_, val) => handleUpdate(row.rowKey, { id: val?.id ?? 0 })}
            disabled={viewOnly}
            size="small"
            fullWidth
          />
        );
      },
    };

    const impactCol: GridColDef<OkrRow> = {
      field: 'impact',
      headerName: 'Impact',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <FormControl size="small" fullWidth>
          <Select
            value={row.impact.id ?? 0}
            onChange={(e) => {
              const impactId = Number(e.target.value);
              handleUpdate(row.rowKey, { impact: { id: impactId, impact: getImpact(impactId) } });
            }}
            disabled={viewOnly}
          >
            {IMPACT_OPTIONS.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.impact}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    };

    const actionsCol: GridColDef<OkrRow> = {
      field: 'actions',
      headerName: '',
      width: 48,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <IconButton
          variant="outlined"
          size="small"
          onClick={() => handleDelete(row.rowKey)}
          aria-label="Remove business impact"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      ),
    };

    return viewOnly ? [nameCol, impactCol] : [nameCol, impactCol, actionsCol];
  }, [okrs, selectedIds, viewOnly, handleUpdate, handleDelete]);

  return (
    <Box>
      <Typography variant="subtitle2">Business Impacted</Typography>
      {!viewOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton variant="outlined" color="primary" size="small" onClick={handleAdd}>
            <Add fontSize="small" />
          </IconButton>
        </Box>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.rowKey}
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        aria-label="Business Impacted"
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': { py: 0.5, alignItems: 'center' },
        }}
      />
    </Box>
  );
}
